import React from "react";
import { Mail } from "lucide-react";
import type { ChatDigestFrequency } from "../../backend/api/chat";
import {
  useChatPreferences,
  useUpdateChatPreferences,
} from "../../backend/queries/chat";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const OPTIONS: { value: ChatDigestFrequency; label: string }[] = [
  { value: "hourly", label: "At most once an hour" },
  { value: "daily", label: "At most once a day" },
  { value: "off", label: "Never" },
];

/**
 * How often the unread message email arrives (SCRUM-61). The email lists who
 * wrote and how many messages, never what they said.
 */
export function ChatEmailPreference() {
  const preferences = useChatPreferences();
  const update = useUpdateChatPreferences();

  return (
    <Card>
      <CardHeader className="p-4 sm:p-5">
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" aria-hidden="true" />
          Message emails
        </CardTitle>
        <CardDescription>
          When messages wait unread for more than 15 minutes, we email you a
          summary of who wrote. The email never includes what they said.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0 sm:max-w-sm sm:p-5 sm:pt-0">
        <Label htmlFor="chat-digest-frequency">Email me</Label>
        <Select
          value={preferences.data?.digest_frequency}
          disabled={!preferences.data || update.isPending}
          onValueChange={(value) =>
            update.mutate({ digest_frequency: value as ChatDigestFrequency })
          }
        >
          <SelectTrigger id="chat-digest-frequency">
            <SelectValue
              placeholder={preferences.isError ? "Could not load" : "Loading"}
            />
          </SelectTrigger>
          <SelectContent>
            {OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {update.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Your choice could not be saved. Try again.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
